import React, { useState, useEffect, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AsyncSelect from "react-select/async";
import { FaSearch } from "react-icons/fa";
import { TfiKey } from "react-icons/tfi";
import {
  MdDelete,
  MdVisibility,
  MdModeEditOutline,
  MdRefresh,
  MdVerified,
} from "react-icons/md";
import {
  FiPlusCircle,
  FiMinusCircle,
  FiUploadCloud,
  FiInfo,
} from "react-icons/fi";
import {
  getItSalesPartners,
  addItSalesPartner,
  editItSalesPartner,
  deleteItSalesPartner,
  changeItSalesStatus,
  getLicenseslist,
  resendItSalesVerificationEmail,
} from "../../redux/actions/itSalesActions";

import { toast } from "react-toastify";
import "../../styles/InputSheet.css";
import "../../styles/ReactSelect.css";
import "../../styles/FileUpload.css";
import {
  getPhoneValidationRules,
  validatePhoneNumber,
} from "../../../utils/phoneValidation";
import { GoUnverified } from "react-icons/go";
import DeleteConfirmModal from "../../modals/DeleteConfirmModal";
import StatusConfirmModal from "../../modals/StatusConfirmModal";
import LogoPreviewModal from "../../modals/LogoPreviewModal";
import ItSalesPartnersTable from "./ItSalesPartnersTable";
import PasswordResetModal from "../../modals/PasswordResetModal";
import LicenseCreateModal from "../../modals/LicenseCreateModal";
import { Offcanvas } from "react-bootstrap";
import {
  MdEmail,
  MdPhone,
  MdLocationOn,
  MdDescription,
  MdAttachMoney,
  MdCalendarToday,
} from "react-icons/md";

const ItSalesPartners = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    partners = [],
    loading = false,
    formLoading = false,
    error = null,
    pagination = {},
    success = false,
    passwordResetStatus = null,
  } = useSelector((state) => state.itSales || {});
  const { user } = useSelector((state) => state.auth || {});
  const { licenses = [] } = useSelector((state) => state.itSales || {});
  const adminId = user?.id || user?._id;
  const [currentPage, setCurrentPage] = useState(1);
  const [showInputForm, setShowInputForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [copiedId, setCopiedId] = useState(null);

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    partner: null,
  });
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    partner: null,
    newStatus: null,
  });
  const [selectedFileName, setSelectedFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [flagLink, setFlagLink] = useState("us");
  const [countryCode, setCountryCode] = useState("+1");
  const [descriptionWordCount, setDescriptionWordCount] = useState(0);
  const [logoPreviewModal, setLogoPreviewModal] = useState({
    isOpen: false,
    imageUrl: "",
    imageName: "",
  });

  const [passwordResetModal, setPasswordResetModal] = useState({
    isOpen: false,
    currentPassword: "",
  });

  const [viewDetailsOffcanvas, setViewDetailsOffcanvas] = useState({
    isOpen: false,
    partner: null,
  });
  const [licenseFields, setLicenseFields] = useState([{ id: 0, value: "" }]);
  const [licenseCreateModal, setLicenseCreateModal] = useState(false);

  const {
    handleSubmit,
    formState: { errors },
    reset,
    register,
    watch,
    control,
    setValue,
    clearErrors,
  } = useForm({
    defaultValues: {
      companyName: "",
      email: "",
      firstName: "",
      lastName: "",
      contactNumber: "",
      country: "",
      currency: "",
      logo: null,
      almataSharedDiscription: "",
      licenseCount: "",
      stripeLicenseKeyId: "",
      status: "active",
      plainPassword: "",
    },
  });

  const selectedCountry = watch("country");
   const almataDescription = watch("almataSharedDiscription");

  useEffect(() => {
    if (almataDescription) {
      const words = almataDescription.trim().split(/\s+/).filter((word) => word.length > 0);
      setDescriptionWordCount(words.length);
    } else {
      setDescriptionWordCount(0);
    }
  }, [almataDescription]);

  const loadCountryOptions = useCallback(async (inputValue) => {
    try {
      const response = inputValue
        ? await fetch(
            `https://restcountries.com/v3.1/name/${inputValue}?fields=name,currencies,cca2,cca3,ccn3,idd`,
          )
        : await fetch(
            "https://restcountries.com/v3.1/all?fields=name,currencies,cca2,cca3,ccn3,idd",
          );
      const data = await response.json();
      return data.slice(0, 10).map((country) => ({
        value: country.name.common,
        label: country.name.common,
        isoCode: country.cca2,
        countryCode: country.idd.root
          ? country.idd.root +
            (country.idd.suffixes?.length
              ? country.name.common === "United States"
                ? ""
                : country?.idd?.suffixes?.[0]
              : "")
          : "",
      }));
    } catch (error) {
      console.error("Error loading countries:", error);
      return [];
    }
  }, []);

  const loadLicenseOptions = useCallback(
    async (inputValue) => {
      return (Array.isArray(licenses) ? licenses : [])
        .filter((license) => {
          // Filter by search input
          const matchesSearch =
            !inputValue ||
            license.name?.toLowerCase().includes(inputValue.toLowerCase());

          // Filter out assigned licenses (only show unassigned ones)
          const isUnassigned = !license.assigned;

          return matchesSearch && isUnassigned;
        })
        .slice(0, 10)
        .map((license) => ({
          value: license._id,
          label: license.name,
          licenseData: license,
        }));
    },
    [licenses],
  );

  const handleCountryChange = useCallback(
    (selectedOption, { action }) => {
      if (action === "clear") {
        setValue("country", "");
        setValue("currency", "");
        setFlagLink("us");
        setCountryCode("+1");
        return null;
      }
      setValue("country", selectedOption?.value || "");

      let currency = "USD";
      if (selectedOption?.value === "Japan") currency = "JPY";
      else if (selectedOption?.value === "Singapore") currency = "SGD";
      else if (selectedOption?.value === "Hong Kong") currency = "HKD";
      else if (selectedOption?.value === "Australia") currency = "AUD";
      else if (selectedOption?.value === "Austria") currency = "EUR";

      setValue("currency", currency);

      if (selectedOption) {
        setFlagLink(selectedOption.isoCode || "us");
        setCountryCode(selectedOption.countryCode || "+1");
      }

      return selectedOption;
    },
    [setValue],
  );

  // const handleLicenseChange = useCallback(
  //   (selectedOption, { action }, fieldId) => {
  //     const updatedFields = licenseFields.map((field) =>
  //       field.id === fieldId
  //         ? {
  //             ...field,
  //             value: action === "clear" ? "" : selectedOption?.value || "",
  //           }
  //         : field
  //     );
  //     setLicenseFields(updatedFields);

  //     // Update form value - use first license for validation, but store all in state
  //     const selectedLicenses = updatedFields.filter((field) => field.value);
  //     setValue(
  //       "stripeLicenseKeyId",
  //       selectedLicenses.length > 0 ? selectedLicenses[0].value : ""
  //     );

  //     // Clear validation error if at least one license is selected
  //     if (selectedLicenses.length > 0) {
  //       clearErrors("stripeLicenseKeyId");
  //     }

  //     return selectedOption;
  //   },
  //   [setValue, licenseFields, clearErrors]
  // );

  const addLicenseField = useCallback(() => {
    const lastField = licenseFields[licenseFields.length - 1];
    if (lastField && lastField.value) {
      const newId = Math.max(...licenseFields.map((f) => f.id)) + 1;
      setLicenseFields([...licenseFields, { id: newId, value: "" }]);
    } else {
      toast.error(
        "Please select a license in the current field before adding a new one",
      );
    }
  }, [licenseFields]);

  const removeLicenseField = useCallback(
    (fieldId) => {
      if (licenseFields.length > 1) {
        const updatedFields = licenseFields.filter(
          (field) => field.id !== fieldId,
        );
        setLicenseFields(updatedFields);

        // Update form value
        const selectedLicenses = updatedFields.filter((field) => field.value);
        setValue(
          "stripeLicenseKeyId",
          selectedLicenses.length > 0 ? selectedLicenses[0].value : "",
        );
      }
    },
    [licenseFields, setValue],
  );

  const handleMultiLicenseChange = useCallback(
    (selectedOptions) => {
      const newFields = selectedOptions.map((option, index) => ({
        id: index,
        value: option.value,
      }));

      setLicenseFields(
        newFields.length > 0 ? newFields : [{ id: 0, value: "" }],
      );

      if (newFields.length > 0) {
        setValue("stripeLicenseKeyId", newFields[0].value);
        clearErrors("stripeLicenseKeyId");
      } else {
        setValue("stripeLicenseKeyId", "");
      }
    },
    [setValue, clearErrors],
  );

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      if (value.length === 0 || (value.length > 0 && value[0] !== " ")) {
        setSearchTerm(value);
        setCurrentPage(1);
      } else if (searchTerm.length > 0) {
        setSearchTerm(value);
        setCurrentPage(1);
      }
    },
    [searchTerm],
  );

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setSelectedFileName(file.name);
    } else {
      setSelectedFile(null);
      setSelectedFileName("");
    }
  }, []);

  const handleLogoPreview = useCallback(() => {
    let imageUrl = "";
    let imageName = "";

    if (selectedFile) {
      imageUrl = URL.createObjectURL(selectedFile);
      imageName = selectedFile.name;
    } else if (editingPartner?.logo) {
      imageUrl = editingPartner.logo;
      imageName = editingPartner.logo.split("/").pop();
    }

    setLogoPreviewModal({ isOpen: true, imageUrl, imageName });
  }, [selectedFile, editingPartner?.logo]);

  useEffect(() => {
    if (adminId) {
      dispatch(
        getItSalesPartners({
          limit: 10,
          page: currentPage,
          search: searchTerm,
          status: statusFilter,
          createdBy: adminId,
        }),
      );
    }
  }, [dispatch, currentPage, searchTerm, statusFilter, adminId]);

  useEffect(() => {
    dispatch(getLicenseslist());
  }, [dispatch]);

  useEffect(() => {
    if (!editingPartner && !selectedCountry && showInputForm) {
      setValue("country", "United States");
      setValue("currency", "USD");
      setFlagLink("us");
      setCountryCode("+1");
    }
  }, [editingPartner, setValue, selectedCountry, showInputForm]);

  const onSubmit = async (data) => {
    if (formLoading) return;

    if (editingPartner) {
      const editPayload = {
        _id: editingPartner._id,
        companyName: data.companyName,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        contactNumber: data.contactNumber,
        country: data.country,
        countryCode,
        flagLink: `https://flagcdn.com/${flagLink?.toLowerCase()}.svg`,
        currency: data.currency,
        almataSharedDiscription: data.almataSharedDiscription,
        licenseCount: data.licenseCount,
        stripeLicenseKeyId: licenseFields
          .filter((field) => field.value)
          .map((field) => ({ licenseId: field.value })),
        ...(selectedFile && { logo: selectedFile }),
        refreshParams: {
          limit: 10,
          page: currentPage,
          search: searchTerm,
          status: statusFilter,
          createdBy: adminId,
        },
      };
      dispatch(editItSalesPartner(editPayload));
    } else {
      const { _id, status, plainPassword, stripeLicenseKeyId, ...formData } = data;
      const addPayload = {
        ...formData,
        stripeLicenseKeyId: licenseFields
          .filter((field) => field.value)
          .map((field) => ({ licenseId: field.value })),
        ...(selectedFile && { logo: selectedFile }),
        countryCode,
        createdByModel: "itsalespartner",
        createdBy: adminId,
        flagLink: `https://flagcdn.com/${flagLink?.toLowerCase()}.svg`,
        refreshParams: {
          limit: 10,
          page: currentPage,
          search: searchTerm,
          status: statusFilter,
          createdBy: adminId,
        },
      };
      dispatch(addItSalesPartner(addPayload));
    }
  };

  const resetFormToDefault = useCallback(() => {
    setEditingPartner(null);
    reset({
      companyName: "",
      email: "",
      firstName: "",
      lastName: "",
      contactNumber: "",
      country: "",
      currency: "",
      logo: null,
      almataSharedDiscription: "",
      licenseCount: "",
      stripeLicenseKeyId: "",
      status: "active",
      plainPassword: "",
    });
    setSelectedFileName("");
    setSelectedFile(null);
    setFlagLink("us");
    setCountryCode("+1");
    setDescriptionWordCount(0);
    setLicenseFields([{ id: 0, value: "" }]);
  }, [reset]);

  const handleViewDetails = (partner) => {
    setViewDetailsOffcanvas({
      isOpen: true,
      partner,
    });
  };

  // const handleTableRowClick = (partner) => {
  //   navigate("/itoemdetails", { state: { partner } });
  // };

   const handleTableRowClick = (partner, pageNumber, status) => {
    navigate("/itsales-split-view", { state: { partner, partnerPageNumber: pageNumber || 1, partnerStatus: status || "active" } });
  };
  const handleViewDetailsOffcanvasClose = () => {
    setViewDetailsOffcanvas({
      isOpen: false,
      partner: null,
    });
  };

  useEffect(() => {
    if (success && !formLoading) {
      setEditingPartner(null);
      reset({
        companyName: "",
        email: "",
        firstName: "",
        lastName: "",
        contactNumber: "",
        country: "",
        currency: "",
        logo: null,
        almataSharedDiscription: "",
        licenseCount: "",
        stripeLicenseKeyId: "",
        status: "active",
        plainPassword: "",
      });
      setSelectedFileName("");
      setSelectedFile(null);
      setFlagLink("us");
      setCountryCode("+1");
      setDescriptionWordCount(0);
      setLicenseFields([{ id: 0, value: "" }]);
      dispatch(getLicenseslist());
      dispatch({ type: 'RESET_ITSALES_SUCCESS' });
    }
  }, [success, formLoading, reset, dispatch]);

  useEffect(() => {
    if (passwordResetStatus === 200) {
      dispatch(
        getItSalesPartners({
          limit: 10,
          page: currentPage,
          search: searchTerm,
          status: statusFilter,
          createdBy: adminId,
        }),
      );
    }
  }, [passwordResetStatus, dispatch, currentPage, searchTerm, statusFilter, adminId]);

  const handleEditClick = useCallback(
    async (partner) => {
      try {
        setEditingPartner(partner);
        const formData = {
          ...partner,
          stripeLicenseKeyId:
            Array.isArray(partner.stripeLicenseKeyId) &&
            partner.stripeLicenseKeyId.length > 0
              ? partner.stripeLicenseKeyId[0].licenseId ||
                partner.stripeLicenseKeyId[0].license?._id ||
                ""
              : partner.stripeLicenseKeyId || "",
        };

        if (
          Array.isArray(partner.stripeLicenseKeyId) &&
          partner.stripeLicenseKeyId.length > 0
        ) {
          const licenseIds = partner.stripeLicenseKeyId.map((item, index) => ({
            id: index,
            value: item.license?._id || item.licenseId || "",
          }));
          setLicenseFields(
            licenseIds.length > 0 ? licenseIds : [{ id: 0, value: "" }],
          );
        } else {
          setLicenseFields([
            { id: 0, value: formData.stripeLicenseKeyId || "" },
          ]);
        }

        reset(formData);
        setShowInputForm(true);

        if (partner.country) {
          try {
            const response = await fetch(
              `https://restcountries.com/v3.1/name/${partner.country}?fields=name,currencies,cca2,cca3,ccn3,idd`,
            );
            const data = await response.json();
            if (data && data.length > 0) {
              const country = data[0];
              setFlagLink(country.cca2 || "us");
              setCountryCode(
                country.idd.root
                  ? country.idd.root +
                      (country.idd.suffixes?.length
                        ? country.name.common === "United States"
                          ? ""
                          : country?.idd?.suffixes?.[0]
                        : "")
                  : "+1",
              );
            }
          } catch (error) {
            console.error("Error fetching country data:", error);
            setFlagLink("us");
            setCountryCode("+1");
          }
        }
      } catch (error) {
        console.error("Error in handleEditClick:", error);
        toast.error("Failed to load partner data for editing");
      }
    },
    [reset],
  );

  const removePartner = useCallback((partner) => {
    setDeleteModal({ isOpen: true, partner });
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    dispatch(
      deleteItSalesPartner({
        id: deleteModal.partner._id,
        refreshParams: {
          limit: 10,
          page: currentPage,
          search: searchTerm,
          status: statusFilter,
          createdBy: adminId,
        },
      }),
    );
    setDeleteModal({ isOpen: false, partner: null });
  }, [
    dispatch,
    deleteModal.partner,
    currentPage,
    searchTerm,
    statusFilter,
    adminId,
  ]);

  const handleStatusChange = useCallback((partner) => {
    const newStatus = partner.status === "active" ? "inactive" : "active";
    setStatusModal({ isOpen: true, partner, newStatus });
  }, []);

  const handleStatusConfirm = useCallback(() => {
    dispatch(
      changeItSalesStatus({
        itSalesId: statusModal.partner._id,
        status: statusModal.newStatus,
        refreshParams: {
          limit: 10,
          page: currentPage,
          search: searchTerm,
          status: statusFilter,
          createdBy: adminId,
        },
      }),
    );
    setStatusModal({ isOpen: false, partner: null, newStatus: null });
  }, [
    dispatch,
    statusModal.partner,
    statusModal.newStatus,
    currentPage,
    searchTerm,
    statusFilter,
    adminId,
  ]);

  const handlePasswordResetClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const currentPasswordValue =
      watch("plainPassword") || editingPartner?.plainPassword || "";
    setPasswordResetModal({ isOpen: true, currentPassword: currentPasswordValue });
  };

  const handlePasswordResetSave = (newPassword) => {
    setValue("plainPassword", newPassword);
    setPasswordResetModal({ isOpen: false, currentPassword: "" });
  };

  const handlePasswordResetClose = () => {
    setPasswordResetModal({ isOpen: false, currentPassword: "" });
  };

  const handleFormReset = useCallback(() => {
    setEditingPartner(null);
    setPasswordResetModal({ isOpen: false, currentPassword: "" });
    reset({
      companyName: "",
      email: "",
      firstName: "",
      lastName: "",
      contactNumber: "",
      country: "",
      currency: "",
      logo: null,
      almataSharedDiscription: "",
      licenseCount: "",
      stripeLicenseKeyId: "",
      status: "active",
      plainPassword: "",
    });
    setSelectedFileName("");
    setSelectedFile(null);
    setFlagLink("us");
    setCountryCode("+1");
    setDescriptionWordCount(0);
    setLicenseFields([{ id: 0, value: "" }]);
  }, [reset]);
   const handleResendVerification = (itsalesId) => {
      dispatch(resendItSalesVerificationEmail(itsalesId));
    };

  return (
    <div className="page-container">
      <div className="page-card scroll-container">
        <div className="form-header">
          <h2>IT Sales Partners Management</h2>
          <div
            onClick={() => setShowInputForm(!showInputForm)}
            className="cursor-pointer info_pointer toggle-btn fs-5 fw-bolder"
            title={showInputForm ? "Hide Input Form" : "Show Input Form"}
          >
            {showInputForm ? <FiMinusCircle /> : <FiPlusCircle />}
          </div>
        </div>

        {showInputForm && (
          <div className="input-form-section">
            <form onSubmit={handleSubmit(onSubmit)} className="input-form">
              <div className="form-grid">
                <div className="form-group">
                  <label>
                    Company Name <span className="text-danger">*</span>
                  </label>
                  <input
                    {...register("companyName", {
                      required: "Company name is required",
                      validate: (value) =>
                        value.trim() !== "" || "Company name is required",
                    })}
                    className={`form-input ${
                      errors.companyName ? "error" : ""
                    }`}
                    placeholder="Enter company name"
                  />
                  {errors.companyName && (
                    <span className="error-text">
                      {errors.companyName.message}
                    </span>
                  )}
                </div>

                <div className="form-group" style={{ position: "relative" }}>
                  <label>
                    Email <span className="text-danger">*</span>{" "}
                  </label>
                  <input
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value:
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: "Please enter a valid email address",
                      },
                    })}
                    type="email"
                    className={`form-input ${errors.email ? "error" : ""} ${
                      editingPartner?.status === "inactive" ? "disabled" : ""
                    }`}
                    placeholder="Enter email"
                    disabled={
                      editingPartner?.status === "inactive" || editingPartner
                    }
                    style={{
                      backgroundColor:
                        editingPartner?.status === "inactive"
                          ? "#f8f9fa"
                          : "" || editingPartner
                            ? "#f8f9fa"
                            : "",
                      paddingRight: "35px",
                    }}
                  />
                  {editingPartner &&
                    (editingPartner.verified ? (
                      <MdVerified
                        className="position-absolute"
                        style={{
                          right: "5px",
                          top: "66%",
                          transform: "translateY(-50%)",
                          color: "#28a745",
                          fontSize: "18px",
                          cursor: "pointer",
                        }}
                        title="Email Verified"
                      />
                    ) : (
                      <GoUnverified
                        className="position-absolute"
                        style={{
                          right: "5px",
                          top: "66%",
                          transform: "translateY(-50%)",
                          color: "#dc3545",
                          fontSize: "18px",
                          cursor: "pointer",
                        }}
                        title="Email Not Verified"
                      />
                    ))}
                  {errors.email && (
                    <span className="error-text">{errors.email.message}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    First Name <span className="text-danger">*</span>
                  </label>
                  <input
                    {...register("firstName", {
                      required: "First name is required",
                      validate: (value) =>
                        value.trim() !== "" || "First name is required",
                    })}
                    className={`form-input ${errors.firstName ? "error" : ""}`}
                    placeholder="Enter first name"
                  />
                  {errors.firstName && (
                    <span className="error-text">
                      {errors.firstName.message}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    Last Name <span className="text-danger">*</span>
                  </label>
                  <input
                    {...register("lastName", {
                      required: "Last name is required",
                      validate: (value) =>
                        value.trim() !== "" || "Last name is required",
                    })}
                    className={`form-input ${errors.firstName ? "error" : ""}`}
                    placeholder="Enter last name"
                  />
                  {errors.lastName && (
                    <span className="error-text">
                      {errors.lastName.message}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    Country/Currency <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="country"
                    control={control}
                    rules={{ required: "Country is required" }}
                    render={({ field: { onChange, value, ...field } }) => (
                      <AsyncSelect
                        {...field}
                        value={
                          value
                            ? {
                                value,
                                label: `${value} (${
                                  watch("currency") || "USD"
                                })`,
                              }
                            : null
                        }
                        loadOptions={loadCountryOptions}
                        onChange={(selectedOption, action) => {
                          handleCountryChange(selectedOption, action);
                          onChange(selectedOption?.value || "");
                        }}
                        placeholder="Search country..."
                        className={errors.country ? "error" : ""}
                        classNamePrefix="react-select"
                        isClearable
                        defaultOptions
                        noOptionsMessage={() => "No countries found"}
                        loadingMessage={() => "Loading countries..."}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: "30px",
                            height: "30px",
                            border: errors.country
                              ? "1px solid #dc3545"
                              : "1px solid #ddd",
                            borderRadius: "4px",
                          }),
                        }}
                      />
                    )}
                  />
                  <input {...register("currency")} type="hidden" />
                  {errors.country && (
                    <span className="error-text">{errors.country.message}</span>
                  )}
                </div>

                <div className="form-group" style={{ position: "relative" }}>
                  <label>
                    Contact Number <span className="text-danger">*</span>
                  </label>
                  <div style={{ position: "relative" }}>
                    {flagLink && countryCode && (
                      <div
                        className="position-absolute d-flex align-items-center"
                        style={{
                          left: "8px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          zIndex: 2,
                          borderRight: "1px solid #ddd",
                          paddingRight: "8px",
                          height: "24px",
                          gap: "4px",
                          background: "#fff",
                        }}
                      >
                        <img
                          src={`https://flagcdn.com/${flagLink?.toLowerCase()}.svg`}
                          alt="flag"
                          width={20}
                          height={14}
                          style={{ objectFit: "cover" }}
                        />
                        <span style={{ fontSize: "12px", color: "#666" }}>
                          ({countryCode})
                        </span>
                      </div>
                    )}
                    <input
                      {...register("contactNumber", {
                        required: "Contact number is required",
                        validate: (value) => {
                          const result = validatePhoneNumber(
                            value,
                            selectedCountry,
                          );
                          return result === true ? true : result;
                        },
                      })}
                      type="tel"
                      className={`form-input ${
                        errors.contactNumber ? "error" : ""
                      }`}
                      placeholder="Enter contact number"
                      maxLength={(() => {
                        const rules = getPhoneValidationRules(selectedCountry);
                        return rules.max;
                      })()}
                      onInput={(e) => {
                        let value = e.target.value.replace(/[^0-9]/g, "");
                        const rules = getPhoneValidationRules(selectedCountry);

                        // Prevent input beyond max length
                        if (value.length > rules.max) {
                          value = value.slice(0, rules.max);
                        }

                        e.target.value = value;
                        setValue("contactNumber", value);
                      }}
                      disabled={editingPartner?.status === "inactive"}
                      style={{
                        paddingLeft: flagLink && countryCode ? "85px" : "12px",
                        height: "30px",
                        border: errors.contactNumber
                          ? "1px solid #dc3545"
                          : "1px solid #ddd",
                        borderRadius: "4px",
                        backgroundColor:
                          editingPartner?.status === "inactive"
                            ? "#f8f9fa"
                            : "#fff",
                        width: "100%",
                      }}
                    />
                  </div>
                  {errors.contactNumber && (
                    <span
                      className="error-text"
                      style={{
                        position: "absolute",
                        bottom: "-18px",
                        left: "0",
                        fontSize: "12px",
                        color: errors.contactNumber.message.includes("/")
                          ? "#ef4444"
                          : "#ef4444",
                      }}
                    >
                      {errors.contactNumber.message.includes("/")
                        ? `Digits entered: ${errors.contactNumber.message}`
                        : errors.contactNumber.message}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label>Logo</label>
                  <div className="file-input-wrapper d-flex align-items-center">
                    <div className="flex-grow-1 position-relative">
                      <input
                        type="file"
                        accept="image/*"
                        className="file-input"
                        id="logo-upload"
                        onChange={handleFileChange}
                      />
                      <label htmlFor="logo-upload" className="file-input-label">
                        <span>
                          {(() => {
                            const fileName =
                              selectedFileName ||
                              (editingPartner?.logo
                                ? editingPartner.logo.split("/").pop()
                                : "Upload Image");
                            return fileName.length > 22
                              ? fileName.substring(0, 22) + "..."
                              : fileName;
                          })()}
                        </span>
                        {!(selectedFile || editingPartner?.logo) && (
                          <FiUploadCloud className="upload-icon" />
                        )}
                      </label>
                      {(selectedFile || editingPartner?.logo) && (
                        <MdVisibility
                          className="info_pointer text-secondary text-primary"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleLogoPreview();
                          }}
                          title="Preview Logo"
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            fontSize: "22px",
                            background: "white",
                            padding: "4px",
                            borderRadius: "4px",
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    License Count <span className="text-danger">*</span>
                  </label>
                  <div className="licensecounter-wapper">
                    <button
                      type="button"
                      className="licensecounter-btn"
                      onClick={() =>
                        setValue(
                          "licenseCount",
                          Math.max((watch("licenseCount") || 0) - 1, 0),
                        )
                      }
                    >
                      -
                    </button>

                    <input
                      {...register("licenseCount", {
                        required: "License count is required",
                        pattern: {
                          value: /^[0-9]+$/,
                          message: "Please enter a valid number",
                        },
                        value: 1,
                      })}
                      type="text"
                      className={`form-input text-center w-100 ${
                        errors.licenseCount ? "error" : ""
                      }`}
                    />

                    <button
                      type="button"
                      className="licensecounter-btn"
                      onClick={() =>
                        setValue(
                          "licenseCount",
                          (Number(watch("licenseCount")) || 0) + 1,
                        )
                      }
                    >
                      +
                    </button>
                  </div>

                  {errors.licenseCount && (
                    <span className="error-text">
                      {errors.licenseCount.message}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>
                    License Keys <span className="text-danger">*</span>
                  </label>
                  <AsyncSelect
                    key={licenses.length} // Force re-render when licenses change
                    isMulti
                    isDisabled={!!editingPartner}
                    value={licenseFields
                      .filter((field) => field.value)
                      .map((field) => {
                        // First try to find in licenses array
                        const selectedLicense = licenses.find(
                          (l) => l._id === field.value,
                        );
                        if (selectedLicense) {
                          return {
                            value: selectedLicense._id,
                            label: selectedLicense.name,
                          };
                        }

                        // If editing, try to find in partner's license data
                        if (editingPartner?.stripeLicenseKeyId) {
                          const partnerLicense =
                            editingPartner.stripeLicenseKeyId.find(
                              (item) => item.license?._id === field.value,
                            );
                          if (partnerLicense?.license) {
                            return {
                              value: partnerLicense.license._id,
                              label: partnerLicense.license.name,
                            };
                          }
                        }

                        return null;
                      })
                      .filter(Boolean)}
                    loadOptions={loadLicenseOptions}
                    onChange={(selectedOptions) => {
                      handleMultiLicenseChange(selectedOptions || []);
                    }}
                    placeholder="Search and select licenses..."
                    className={errors.stripeLicenseKeyId ? "error" : ""}
                    classNamePrefix="react-select"
                    isClearable
                    defaultOptions={true}
                    noOptionsMessage={() => "No licenses found"}
                    loadingMessage={() => "Loading licenses..."}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    components={{
                      Option: ({
                        children,
                        innerProps,
                        isSelected,
                        isFocused,
                      }) => {
                        return (
                          <div
                            {...innerProps}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 12px",
                              cursor: "pointer",
                              backgroundColor: isFocused ? "#f8f9fa" : "white",
                              opacity: isSelected ? 0.6 : 1,
                              pointerEvents: isSelected ? "none" : "auto",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              readOnly
                              style={{
                                marginRight: "8px",
                                cursor: isSelected ? "not-allowed" : "pointer",
                              }}
                            />
                            <span
                              style={{
                                color: isSelected ? "#6c757d" : "#212529",
                                textDecoration: isSelected
                                  ? "line-through"
                                  : "none",
                              }}
                            >
                              {children}
                            </span>
                          </div>
                        );
                      },

                      MenuList: ({ children, innerRef, innerProps }) => (
                        <div ref={innerRef} {...innerProps}>
                          {children}
                          <div
                            onClick={() => setLicenseCreateModal(true)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 12px",
                              cursor: "pointer",
                              backgroundColor: "#f8f9fa",
                              borderTop: "1px solid #dee2e6",
                              color: "#007bff",
                              fontWeight: "500",
                            }}
                          >
                            <FiPlusCircle
                              style={{ marginRight: "8px", fontSize: "14px" }}
                            />
                            <span>Add More License Keys</span>
                          </div>
                        </div>
                      ),
                    }}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "40px",
                        height: "40px",
                        width: "16vw",
                        maxWidth: "16vw",
                        border: errors.stripeLicenseKeyId
                          ? "1px solid #dc3545"
                          : "1px solid #ddd",
                        borderRadius: "4px",
                        backgroundColor: editingPartner ? "#f8f9fa" : "white",
                        cursor: editingPartner ? "not-allowed" : "default",
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        display: "flex",
                        flexWrap: "nowrap",
                        overflowX: "auto",
                        overflowY: "hidden",
                        height: "30px",
                        width: "calc(100% - 40px)",
                        maxWidth: "calc(100% - 40px)",
                        padding: "2px 8px",
                        scrollbarWidth: "none", // Firefox
                        msOverflowStyle: "none", // IE/Edge
                        "&::-webkit-scrollbar": {
                          display: "none", // Chrome/Safari
                        },
                      }),
                      multiValue: (base) => ({
                        ...base,
                        flexShrink: 0,
                        margin: "1px 2px",
                        fontSize: "13px",
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        whiteSpace: "nowrap",
                        padding: "2px 6px",
                      }),
                    }}
                  />

                  <Controller
                    name="stripeLicenseKeyId"
                    control={control}
                    rules={{ required: "At least one license is required" }}
                    render={() => null}
                  />

                  {errors.stripeLicenseKeyId && (
                    <span className="error-text">
                      {errors.stripeLicenseKeyId.message}
                    </span>
                  )}
                </div>

                {editingPartner && (
                  <div className="form-group position-relative">
                    <label>Password</label>
                    <input
                      {...register("plainPassword")}
                      type="text"
                      className="form-input"
                      placeholder="Generated password"
                      readOnly
                    />
                    {editingPartner?.status === "active" && (
                      <MdRefresh
                        className="position-absolute"
                        style={{
                          right: "8px",
                          top: "66%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          color: "#007bff",
                          fontSize: "16px",
                          zIndex: 10,
                        }}
                        onClick={(e) => handlePasswordResetClick(e)}
                        title="Reset password"
                      />
                    )}
                  </div>
                )}
              </div>

              <div className="form-group col-4 mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <label>
                      Almata Shared Description{" "}
                      <span className="text-danger">*</span>
                      <span
                        className="text-secondary info_pointer cursor-pointer ms-1"
                        title="You can enter up to 50 words"
                      >
                        <FiInfo />
                      </span>
                    </label>
                  </div>
                  <div className="text-muted small mt-1">
                    Words: {descriptionWordCount}/50
                  </div>
                </div>
                <textarea
                  {...register("almataSharedDiscription", {
                    required: "Almata shared description is required",
                  })}
                  className={`form-input ${
                    errors.almataSharedDiscription ? "error" : ""
                  }`}
                  placeholder="Enter IT Sales description...."
                  rows={4}
                  onChange={(e) => {
                    const words = e.target.value
                      .trim()
                      .split(/\s+/)
                      .filter((word) => word.length > 0);
                    setDescriptionWordCount(words.length);
                    if (e.target.value.trim()) {
                      clearErrors("almataSharedDiscription");
                    }
                  }}
                  onKeyDown={(e) => {
                    const currentText = e.target.value;
                    const words = currentText
                      .trim()
                      .split(/\s+/)
                      .filter((word) => word.length > 0);
                    if (
                      words.length >= 50 &&
                      e.key !== "Backspace" &&
                      e.key !== "Delete" &&
                      !e.key.includes("Arrow")
                    ) {
                      e.preventDefault();
                      toast.error("Maximum 50 words reached");
                    }
                  }}
                />
                {errors.almataSharedDiscription && (
                  <span className="error-text">
                    {errors.almataSharedDiscription.message}
                  </span>
                )}
              </div>

              <div className="form-actions">
                {editingPartner && (
                  <button
                    type="button"
                    className="cancel-btn-deleteModal"
                    onClick={handleFormReset}
                  >
                    Reset
                  </button>
                )}
                <button
                  type="submit"
                  className="add-btn"
                  disabled={formLoading}
                >
                  {formLoading
                    ? "Processing..."
                    : editingPartner
                      ? "Update Partner"
                      : "Add Partner"}
                </button>
              </div>
            </form>
          </div>
        )}

        <ItSalesPartnersTable
          partners={partners}
          loading={loading}
          currentPage={currentPage}
          pagination={pagination}
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          copiedId={copiedId}
          setCopiedId={setCopiedId}
          handleSearchChange={handleSearchChange}
          setStatusFilter={setStatusFilter}
          handleEditClick={handleEditClick}
          removePartner={removePartner}
          setCurrentPage={setCurrentPage}
          handleStatusChange={handleStatusChange}
          handleViewDetails={handleViewDetails}
          handleTableRowClick={handleTableRowClick}
          handleResendVerification={handleResendVerification}
        />
      </div>

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, partner: null })}
        onConfirm={handleDeleteConfirm}
        partnerName={deleteModal.partner?.companyName || ""}
      />

      <StatusConfirmModal
        isOpen={statusModal.isOpen}
        onClose={() =>
          setStatusModal({ isOpen: false, partner: null, newStatus: null })
        }
        onConfirm={handleStatusConfirm}
        partnerName={statusModal.partner?.companyName || ""}
        currentStatus={statusModal.partner?.status || ""}
        newStatus={statusModal.newStatus || ""}
      />

      <LogoPreviewModal
        isOpen={logoPreviewModal.isOpen}
        onClose={() =>
          setLogoPreviewModal({ isOpen: false, imageUrl: "", imageName: "" })
        }
        imageUrl={logoPreviewModal.imageUrl}
        imageName={logoPreviewModal.imageName}
      />

      <PasswordResetModal
        isOpen={passwordResetModal.isOpen}
        onClose={handlePasswordResetClose}
        onSave={handlePasswordResetSave}
        currentPassword={passwordResetModal.currentPassword}
        partnerId={editingPartner?._id}
        partnerType="itSales"
      />

      <LicenseCreateModal
        isOpen={licenseCreateModal}
        onClose={() => setLicenseCreateModal(false)}
        onLicenseCreated={() => {
          // Force refresh of AsyncSelect by clearing and reloading options
          dispatch(getLicenseslist());
          // Force AsyncSelect to reload by clearing its cache
          setTimeout(() => {
            // This will trigger a re-render of the AsyncSelect component
            setLicenseFields([...licenseFields]);
          }, 500);
        }}
      />

      {/* Eye Icon Offcanvas - Partner Details */}
      <Offcanvas
        show={viewDetailsOffcanvas.isOpen}
        onHide={handleViewDetailsOffcanvasClose}
        placement="end"
        style={{ width: "500px" }}
      >
        <Offcanvas.Header className="border-bottom">
          <Offcanvas.Title className="d-flex align-items-center" style={{fontSize:"15px"}}>
            IT Sales Partner Details
          </Offcanvas.Title>
          <button
            type="button"
            className="btn-close"
            onClick={handleViewDetailsOffcanvasClose}
            aria-label="Close"
          />
        </Offcanvas.Header>

        <Offcanvas.Body className="p-0">
          {viewDetailsOffcanvas.partner && (
            <>
              {/* Header Section with Logo and Status */}
              <div className="bg-light p-3 border-bottom">
                <div className="d-flex align-items-center">
                  {viewDetailsOffcanvas.partner.logo && (
                    <img
                      src={viewDetailsOffcanvas.partner.logo}
                      alt="Company Logo"
                      className="rounded me-3"
                      style={{
                        height: "60px",
                        objectFit: "cover",
                        border: "1px solid #ddd",
                        background: "linear-gradient(135deg, #1e40af, #3b82f6)",
                      }}
                    />
                  )}
                  <div>
                    <h6 className="mb-0">
                      {viewDetailsOffcanvas.partner.companyName}
                    </h6>
                    <span
                      className={`badge ${
                        viewDetailsOffcanvas.partner.status === "active"
                          ? "bg-success"
                          : "bg-secondary"
                      }`}
                    >
                      {viewDetailsOffcanvas.partner.status?.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="p-3 border-bottom">
                <h6 className="text-muted mb-3">Contact Information</h6>
                <div className="row g-1">
                  <div className="col-12">
                    <div className="d-flex align-items-center">
                      <MdEmail
                        className="text-muted me-2"
                        style={{ fontSize: "12px" }}
                      />
                      <span>{viewDetailsOffcanvas.partner.email}</span>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="d-flex align-items-center">
                      <span
                        className="text-muted me-2"
                        style={{ fontSize: "10px" }}
                      >
                        👤
                      </span>
                      <span>
                        {viewDetailsOffcanvas.partner.firstName}{" "}
                        {viewDetailsOffcanvas.partner.lastName}
                      </span>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="d-flex align-items-center">
                      <MdPhone
                        className="text-muted me-2"
                        style={{ fontSize: "12px" }}
                      />
                      {viewDetailsOffcanvas.partner.countryCode ? (
                        <div className="d-flex align-items-center">
                          <span style={{ fontSize: "", marginRight: "4px" }}>
                            ({viewDetailsOffcanvas.partner.countryCode})
                          </span>
                          <span>
                            {viewDetailsOffcanvas.partner.contactNumber}
                          </span>
                        </div>
                      ) : (
                        <span>
                          {viewDetailsOffcanvas.partner.contactNumber}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="d-flex align-items-center">
                      <MdLocationOn
                        className="text-muted me-2"
                        style={{ fontSize: "14px" }}
                      />
                      <span>
                        {viewDetailsOffcanvas.partner.country} (
                        {viewDetailsOffcanvas.partner.currency})
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* License Details */}
              {viewDetailsOffcanvas.partner.stripeLicenseKeyId &&
                Array.isArray(
                  viewDetailsOffcanvas.partner.stripeLicenseKeyId,
                ) &&
                viewDetailsOffcanvas.partner.stripeLicenseKeyId.length > 0 && (
                  <div className="p-3 border-bottom">
                    <h6 className="text-muted mb-3">License Details</h6>
                    {viewDetailsOffcanvas.partner.stripeLicenseKeyId.map(
                      (license, index) => (
                        <div key={index} className="card mb-2">
                          <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <h6 className="card-title mb-0">
                                {license.license?.name || "License"}
                              </h6>
                              <span className="badge bg-primary">
                                Count:{" "}
                                {viewDetailsOffcanvas.partner.licenseCount}
                              </span>
                            </div>
                            <small className="text-muted">
                              <span className="text-black fs-6 border px-1 py-0 rounded-2 me-2">
                                <TfiKey />
                              </span>
                              {license?.license?.licenseKey ||
                                license?.license?.licenseKey}
                            </small>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                )}

              {/* Description */}
              <div className="p-3 border-bottom">
                <h6 className="text-muted mb-3">
                  <MdDescription className="me-2" />
                  IT Sales Description
                </h6>
                <p className="mb-0">
                  {viewDetailsOffcanvas.partner.almataSharedDiscription ||
                    "No description available"}
                </p>
              </div>

              {/* Payment Information */}
              <div className="p-3 border-bottom">
                <h6 className="text-muted mb-3">
                  <MdAttachMoney className="me-2" />
                  Payment Information
                </h6>
                <div className="row g-2">
                  <div className="col-6">
                    <small className="text-muted">Payment Currency</small>
                    <div>{viewDetailsOffcanvas.partner.currency}</div>
                  </div>
                  <div className="col-6">
                    <small className="text-muted">Password</small>
                    <div className="d-flex align-items-center">
                      <span className="me-2">••••••••••••</span>
                      <small className="text-success">Available</small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <div className="p-3">
                <h6 className="text-muted mb-3">
                  <MdCalendarToday className="me-2" />
                  Timeline
                </h6>
                <div className="row g-2">
                  <div className="col-12">
                    <small className="text-muted">Created At</small>
                    <div>
                      {new Date(
                        viewDetailsOffcanvas.partner.createdAt,
                      ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                  <div className="col-12">
                    <small className="text-muted">Last Updated</small>
                    <div>
                      {new Date(
                        viewDetailsOffcanvas.partner.updatedAt,
                      ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default ItSalesPartners;
